import { emptySplitApi } from '@/store/emptyApi';
import type {
  ApiPortalV1AssessmentsPlansCultureActionsListApiResponse,
  ApiPortalV1AssessmentsPlansCultureActionsListApiArg,
  ApiPortalV1AssessmentsPlansCultureActionsCreateApiResponse,
  ApiPortalV1AssessmentsPlansCultureActionsCreateApiArg,
  ApiPortalV1AssessmentsPlansCultureActionsRetrieveApiResponse,
  ApiPortalV1AssessmentsPlansCultureActionsRetrieveApiArg,
  ApiPortalV1AssessmentsPlansCultureActionsUpdateApiResponse,
  ApiPortalV1AssessmentsPlansCultureActionsUpdateApiArg,
  ApiPortalV1AssessmentsPlansCultureActionsPartialUpdateApiResponse,
  ApiPortalV1AssessmentsPlansCultureActionsPartialUpdateApiArg,
  ApiPortalV1AssessmentsPlansCultureActionsDestroyApiResponse,
  ApiPortalV1AssessmentsPlansCultureActionsDestroyApiArg,
} from '../../../store/generated/portal/assessments/plans-culture-actions';

export const PLANS_CULTURE_ACTIONS_ROUTES = {
  list: '/api/portal/v1/assessments/plans/culture-actions/',
} as const;

const taggedApi = emptySplitApi.enhanceEndpoints({
  addTagTypes: ['PlansCultureActions'],
});

export const plansCultureActionsApi = taggedApi.injectEndpoints({
  endpoints: builder => ({
    getPlansCultureActions: builder.query<
      ApiPortalV1AssessmentsPlansCultureActionsListApiResponse,
      ApiPortalV1AssessmentsPlansCultureActionsListApiArg
    >({
      query: ({ assessmentPeriodActionPlanId }) => ({
        url: PLANS_CULTURE_ACTIONS_ROUTES.list,
        params: {
          assessment_period_action_plan_id: assessmentPeriodActionPlanId,
        },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { assessmentPeriodActionPlanId } = queryArgs as ApiPortalV1AssessmentsPlansCultureActionsListApiArg;
        return `${endpointName}(${JSON.stringify({ assessmentPeriodActionPlanId })})`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((r: any) => ({ type: 'PlansCultureActions' as const, id: r.id })),
              { type: 'PlansCultureActions' as const, id: 'LIST' },
            ]
          : [{ type: 'PlansCultureActions' as const, id: 'LIST' }],
    }),

    createPlansCultureAction: builder.mutation<
      ApiPortalV1AssessmentsPlansCultureActionsCreateApiResponse,
      ApiPortalV1AssessmentsPlansCultureActionsCreateApiArg
    >({
      query: (arg) => ({
        url: PLANS_CULTURE_ACTIONS_ROUTES.list,
        method: 'POST',
        body: arg.assessmentPeriodActionPlanCultureAction,
      }),
      invalidatesTags: [{ type: 'PlansCultureActions', id: 'LIST' }],
    }),

    getPlansCultureAction: builder.query<
      ApiPortalV1AssessmentsPlansCultureActionsRetrieveApiResponse,
      ApiPortalV1AssessmentsPlansCultureActionsRetrieveApiArg
    >({
      query: ({ id }) => ({
        url: `${PLANS_CULTURE_ACTIONS_ROUTES.list}${id}/`,
      }),
      providesTags: (_result, _error, args) => [{ type: 'PlansCultureActions', id: args.id }],
    }),

    updatePlansCultureAction: builder.mutation<
      ApiPortalV1AssessmentsPlansCultureActionsUpdateApiResponse,
      ApiPortalV1AssessmentsPlansCultureActionsUpdateApiArg
    >({
      query: ({ id, assessmentPeriodActionPlanCultureAction }) => ({
        url: `${PLANS_CULTURE_ACTIONS_ROUTES.list}${id}/`,
        method: 'PUT',
        body: assessmentPeriodActionPlanCultureAction,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'PlansCultureActions', id }],
    }),

    partialUpdatePlansCultureAction: builder.mutation<
      ApiPortalV1AssessmentsPlansCultureActionsPartialUpdateApiResponse,
      ApiPortalV1AssessmentsPlansCultureActionsPartialUpdateApiArg
    >({
      query: ({ id, patchedAssessmentPeriodActionPlanCultureAction }) => ({
        url: `${PLANS_CULTURE_ACTIONS_ROUTES.list}${id}/`,
        method: 'PATCH',
        body: patchedAssessmentPeriodActionPlanCultureAction,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'PlansCultureActions', id }],
    }),

    deletePlansCultureAction: builder.mutation<
      ApiPortalV1AssessmentsPlansCultureActionsDestroyApiResponse,
      ApiPortalV1AssessmentsPlansCultureActionsDestroyApiArg
    >({
      query: ({ id }) => ({
        url: `${PLANS_CULTURE_ACTIONS_ROUTES.list}${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PlansCultureActions', id },
        { type: 'PlansCultureActions', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetPlansCultureActionsQuery,
  useCreatePlansCultureActionMutation,
  useGetPlansCultureActionQuery,
  useUpdatePlansCultureActionMutation,
  usePartialUpdatePlansCultureActionMutation,
  useDeletePlansCultureActionMutation,
} = plansCultureActionsApi;
